import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Share2, Copy, Check, Mail, Twitter, Facebook, MessageCircle } from 'lucide-react';

/**
 * ShareMenu — dropdown with X, Facebook, WhatsApp, Email, and Copy Link.
 * Uses native Web Share API on supported devices as the primary trigger, else opens the menu.
 */
export default function ShareMenu({ title, url, className = '' }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const shareUrl = url || (typeof window !== 'undefined' ? window.location.href : '');
  const encoded = encodeURIComponent(shareUrl);
  const encTitle = encodeURIComponent(title || 'Evermark');

  const items = [
    { label: 'Twitter', Icon: Twitter, href: `https://twitter.com/intent/tweet?text=${encTitle}&url=${encoded}` },
    { label: 'Facebook', Icon: Facebook, href: `https://www.facebook.com/sharer/sharer.php?u=${encoded}` },
    { label: 'WhatsApp', Icon: MessageCircle, href: `https://wa.me/?text=${encTitle}%20${encoded}` },
    { label: 'Email', Icon: Mail, href: `mailto:?subject=${encTitle}&body=${encoded}` },
  ];

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {}
  };

  const onTrigger = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title, url: shareUrl });
        return;
      } catch {}
    }
    setOpen((o) => !o);
  };

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button
        type="button"
        onClick={onTrigger}
        data-testid="share-toggle"
        className="inline-flex items-center gap-2 overline link-underline"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Share2 className="h-3 w-3" /> Share
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.25 }}
            className="absolute z-40 left-0 mt-3 w-56 bg-background border border-border shadow-xl"
            role="menu"
            data-testid="share-menu"
          >
            {items.map(({ label, Icon, href }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noreferrer noopener"
                data-testid={`share-${label.toLowerCase()}`}
                className="flex items-center gap-3 px-4 py-3 text-sm border-b border-border last:border-b-0 hover:bg-foreground/5 transition-colors duration-500"
                role="menuitem"
                onClick={() => setOpen(false)}
              >
                <Icon className="h-4 w-4" /> {label}
              </a>
            ))}
            <button
              type="button"
              onClick={copy}
              data-testid="share-copy"
              className="flex w-full items-center gap-3 px-4 py-3 text-sm hover:bg-foreground/5 transition-colors duration-500"
              role="menuitem"
            >
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              {copied ? 'Link copied' : 'Copy link'}
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
